import React from 'react';
import moment from 'moment';
import { carrierFilter, shipperFilter, masterAccountFilter, } from 'components/design-system/presets';
import { ReferenceFilter, DateRangeWithTypeFilter, MissingDocumentsFilter, ReasonCodeFilter, NeedsReviewFilter, MarketRegionFilter, OpsGroupFilter, QuickpayCarrierFilter, } from 'components/design-system/organisms/AccessorialExceptionFilters';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import { getDefaultDateRangeFilter } from 'helpers/date-range';
// Mirrors DEFAULT_DATE_RANGE_VALUES from the legacy class. Shape accepted by
// getDefaultDateRangeFilter with unspecified optional fields as undefined.
const DEFAULT_DATE_RANGE_VALUES = {
    options: undefined,
    isDashboard: undefined,
    startValue: 2,
    startTimeUnit: 'month',
    endValue: undefined,
    endTimeUnit: undefined,
    startKey: { key: 'created_at' },
};
const DATE_FILTER_TYPE_TO_KEY = {
    requested: 'created_at',
    delivery: 'deliver_after',
};
const DATE_FILTER_TYPE_OPTIONS = [
    { label: 'Requested', value: 'requested' },
    { label: 'Delivery', value: 'delivery' },
];
const INITIAL_DATE_RANGE_VALUE = {
    from: null,
    to: null,
    type: 'requested',
};
const ACCESSORIAL_STATUS_OPTIONS = [
    { label: 'Pending admin', value: 'pending-admin' },
    { label: 'Pending shipper', value: 'pending-shipper' },
    { label: 'Accepted', value: 'accepted' },
    { label: 'Approved', value: 'approved' },
    { label: 'Declined', value: 'declined' },
];
const ACTION_TYPE_OPTIONS = [
    { label: 'Awaiting Admin Review', value: 'Awaiting Admin Review' },
    { label: 'Awaiting Shipper Response', value: 'Awaiting Shipper Response' },
];
const PAYEE_TYPE_OPTIONS = [
    { label: 'Carrier', value: 'carrier' },
    { label: 'Vendor', value: 'vendor' },
    { label: 'Partner', value: 'partner' },
];
const NEEDS_REVIEW_OPTIONS = [
    { label: 'Needs Review', value: 'true' },
    { label: 'No Review', value: 'false' },
];
/**
 * Declarative filter config for the Accessorial Exceptions page.
 *
 * Factory accepts async-loaded option data (accessorials + reasonCodes) so
 * the config can re-render when those arrive. Page memoizes the result:
 *
 *   const FILTERS = useMemo(
 *     () => makeAccessorialExceptionFilters({ accessorials, reasonCodes }),
 *     [accessorials, reasonCodes],
 *   );
 */
export function makeAccessorialExceptionFilters({ accessorials, reasonCodes, }) {
    return [
        // -----------------------------------------------------------------------
        // Reference search (domain organism — multi-ID list parsed from text)
        // -----------------------------------------------------------------------
        {
            id: 'reference',
            label: 'Reference',
            kind: 'autocomplete-multi',
            initialValue: [],
            toApiParam: { name: 'id' },
            render: (ctx) => (React.createElement(ReferenceFilter, { inline: true, value: ctx.value || [], onChange: (v) => ctx.onChange(v) })),
        },
        // -----------------------------------------------------------------------
        // Customer presets (carrier, shipper, master account)
        // -----------------------------------------------------------------------
        masterAccountFilter('master_shipper', 'Master Account'),
        shipperFilter('shipper', 'Shipper'),
        carrierFilter('carrier', 'Carrier'),
        // -----------------------------------------------------------------------
        // Accessorial type (async options via AccessorialTypeFilter organism)
        // -----------------------------------------------------------------------
        {
            id: 'accessorial_type',
            label: 'Accessorial Name',
            kind: 'select-multi',
            initialValue: [],
            options: accessorials.map((a) => ({
                label: a.name,
                value: a.slug || a._id,
            })),
            toApiParam: {
                name: 'accessorial_type',
                transform: (v) => {
                    const arr = v;
                    // Backend only wants the param if a proper subset is selected
                    if (!(arr === null || arr === void 0 ? void 0 : arr.length) || arr.length >= accessorials.length)
                        return undefined;
                    return arr;
                },
            },
        },
        // -----------------------------------------------------------------------
        // Action required
        // -----------------------------------------------------------------------
        {
            id: 'action_type',
            label: 'Action Required',
            kind: 'select-multi',
            initialValue: [],
            options: ACTION_TYPE_OPTIONS,
            toApiParam: { name: 'action_required' },
        },
        // -----------------------------------------------------------------------
        // Shipper / Carrier statuses (same options, different API params)
        // -----------------------------------------------------------------------
        {
            id: 'shipper_status',
            label: 'Shipper Status',
            kind: 'select-multi',
            initialValue: ['pending-admin'],
            options: ACCESSORIAL_STATUS_OPTIONS,
            toApiParam: { name: 'shipper_status' },
        },
        {
            id: 'carrier_status',
            label: 'Carrier Status',
            kind: 'select-multi',
            initialValue: ['pending-admin'],
            options: ACCESSORIAL_STATUS_OPTIONS,
            toApiParam: { name: 'carrier_status' },
        },
        // -----------------------------------------------------------------------
        // Payee type
        // -----------------------------------------------------------------------
        {
            id: 'payee_type',
            label: 'Payee Type',
            kind: 'select-multi',
            initialValue: [],
            options: PAYEE_TYPE_OPTIONS,
            toApiParam: { name: 'payee_type' },
        },
        // -----------------------------------------------------------------------
        // Ops group (domain organism owns options)
        // -----------------------------------------------------------------------
        {
            id: 'ops_group',
            label: 'Operations Group',
            kind: 'select-multi',
            initialValue: [],
            options: [],
            toApiParam: { name: 'operations_group' },
            render: (ctx) => (React.createElement(OpsGroupFilter, { value: ctx.value || [], onChange: (v) => ctx.onChange(v) })),
        },
        // -----------------------------------------------------------------------
        // Date range — type dropdown (Requested → created_at, Delivery →
        // deliver_after) + calendar picker. Matches v1 AccessorialExceptions
        // onDateRangeChange behavior; emits { key, start, end } ISO shape.
        // -----------------------------------------------------------------------
        {
            id: 'date_range',
            label: 'Date Range',
            kind: 'date-range',
            initialValue: INITIAL_DATE_RANGE_VALUE,
            toApiParam: {
                name: 'date_range',
                transform: (v) => {
                    var _a;
                    const range = v;
                    if (!(range === null || range === void 0 ? void 0 : range.from) || !(range === null || range === void 0 ? void 0 : range.to)) {
                        return getDefaultDateRangeFilter(DEFAULT_DATE_RANGE_VALUES);
                    }
                    return {
                        key: DATE_FILTER_TYPE_TO_KEY[(_a = range.type) !== null && _a !== void 0 ? _a : 'requested'],
                        start: moment(range.from).toISOString(),
                        end: moment(range.to).toISOString(),
                    };
                },
            },
            render: (ctx) => {
                var _a;
                return (React.createElement(DateRangeWithTypeFilter, { typeOptions: DATE_FILTER_TYPE_OPTIONS, value: (_a = ctx.value) !== null && _a !== void 0 ? _a : INITIAL_DATE_RANGE_VALUE, onChange: (next) => ctx.onChange(next) }));
            },
        },
        // -----------------------------------------------------------------------
        // Missing documents (feature-gated toggle)
        // -----------------------------------------------------------------------
        {
            id: 'missing_docs',
            label: 'Missing Documents',
            kind: 'toggle',
            initialValue: false,
            featureFlag: () => featureEnabled(CONFIGURED_FEATURES.SSR),
            toApiParam: {
                name: 'missing_docs',
                transform: (v) => (v ? true : undefined),
            },
            render: (ctx) => (React.createElement(MissingDocumentsFilter, { value: !!ctx.value, onChange: (v) => ctx.onChange(v) })),
        },
        // -----------------------------------------------------------------------
        // Reason code (async options)
        // -----------------------------------------------------------------------
        {
            id: 'reason_code',
            label: 'Reason Code',
            kind: 'select-single',
            initialValue: null,
            options: [],
            toApiParam: { name: 'carrier_decline_reason_code' },
            render: (ctx) => (React.createElement(ReasonCodeFilter, { reasonCodes: reasonCodes, value: ctx.value, onChange: (v) => ctx.onChange(v) })),
        },
        // -----------------------------------------------------------------------
        // Needs review (feature-gated select-multi)
        // -----------------------------------------------------------------------
        {
            id: 'needs_review',
            label: 'Auto Apply Status',
            kind: 'select-multi',
            initialValue: [],
            options: NEEDS_REVIEW_OPTIONS,
            featureFlag: () => featureEnabled(CONFIGURED_FEATURES.AUTO_APPLY_ACCESSORIAL),
            toApiParam: { name: 'needs_review' },
            render: (ctx) => (React.createElement(NeedsReviewFilter, { value: ctx.value || [], onChange: (v) => ctx.onChange(v) })),
        },
        // -----------------------------------------------------------------------
        // Market region (domain organism)
        // -----------------------------------------------------------------------
        {
            id: 'market_region',
            label: 'Region',
            kind: 'select-multi',
            initialValue: [],
            options: [],
            toApiParam: { name: 'market_regions' },
            render: (ctx) => (React.createElement(MarketRegionFilter, { value: ctx.value || [], onChange: (v) => ctx.onChange(v) })),
        },
        // -----------------------------------------------------------------------
        // Quickpay carrier (toggle, domain organism)
        // -----------------------------------------------------------------------
        {
            id: 'quickpay_carrier',
            label: 'Quickpay Carriers',
            kind: 'toggle',
            initialValue: false,
            toApiParam: {
                name: 'quickpay_carrier',
                transform: (v) => (v ? true : undefined),
            },
            render: (ctx) => (React.createElement(QuickpayCarrierFilter, { value: !!ctx.value, onChange: (v) => ctx.onChange(v) })),
        },
    ];
}
// Fallback for pages that want the static shape without async deps
// (used when accessorials/reasonCodes haven't loaded yet).
export const EMPTY_FILTER_DEPS = {
    accessorials: [],
    reasonCodes: [],
};
